import { formatRes, seriesOf } from '../catalogue';
import { localVersions } from '../wayback';
import type { SourceDef, TimeEntry } from '../types';
import { probeSeries, type Coverage } from '../coverage';
import { clear, el } from './dom';

/**
 * Horizontal epoch strip for collections. Years that the probe finds empty at the
 * current spot are dimmed rather than hidden — the data is genuinely missing
 * there, and saying so is more useful than silently offering a blank map.
 */
export class TimeStrip {
  readonly root = el('div', { class: 'timestrip', attrs: { hidden: 'hidden' } });
  private readonly tabs = el('div', { class: 'timestrip-tabs' });
  private readonly chips = el('div', { class: 'timestrip-chips' });
  private def?: SourceDef;
  private seriesKey = 'default';
  private activeTime?: string;
  private center: [number, number] = [0, 0];
  private zoom = 14;
  private reloadTimer: ReturnType<typeof setTimeout> | undefined;
  private chipNodes = new Map<string, HTMLElement>();

  constructor(private readonly onPick: (timeId: string) => void) {
    this.root.append(this.tabs, this.chips);
  }

  setCenter(center: [number, number], zoom: number): void {
    this.center = center;
    this.zoom = zoom;
    if (!this.def) return;
    if (this.def.timesMode === 'wayback-local') {
      // The set of versions is a property of the place, so it has to be re-asked
      // for when the place changes.
      clearTimeout(this.reloadTimer);
      const def = this.def;
      this.reloadTimer = setTimeout(() => void this.show(def, this.activeTime, center, zoom), 500);
      return;
    }
    this.probe();
  }

  hide(): void {
    this.root.hidden = true;
    this.def = undefined;
  }

  async show(def: SourceDef, timeId: string | undefined, center: [number, number], zoom = this.zoom): Promise<void> {
    const changedSource = this.def?.id !== def.id;
    this.def = def;
    this.center = center;
    this.zoom = zoom;
    this.activeTime = timeId;

    let series = seriesOf(def);
    if (def.timesMode === 'wayback-local') {
      const times = await localVersions(center, zoom);
      if (this.def?.id !== def.id) return;
      if (!times.length) {
        this.hide();
        return;
      }
      series = [{ key: 'default', label: 'zde dostupné', times }];
      def.times = times;
    }
    if (!series.length) {
      this.hide();
      return;
    }

    if (changedSource || !series.some((s) => s.key === this.seriesKey)) {
      this.seriesKey = series.find((s) => s.times.some((t) => t.id === timeId))?.key ?? series[0].key;
    }

    this.root.hidden = false;
    this.renderTabs(series);
    this.renderChips(series.find((s) => s.key === this.seriesKey)?.times ?? []);
    this.probe();
  }

  private renderTabs(series: { key: string; label: string }[]): void {
    clear(this.tabs);
    this.tabs.hidden = series.length < 2;
    for (const s of series) {
      this.tabs.append(
        el('button', {
          class: s.key === this.seriesKey ? 'timestrip-tab is-on' : 'timestrip-tab',
          attrs: { type: 'button' },
          text: s.label,
          on: {
            click: () => {
              this.seriesKey = s.key;
              if (this.def) void this.show(this.def, this.activeTime, this.center);
            },
          },
        }),
      );
    }
  }

  private renderChips(times: TimeEntry[]): void {
    clear(this.chips);
    this.chipNodes.clear();
    for (const t of times) {
      const chip = el('button', {
        class: t.id === this.activeTime ? 'chip is-active' : 'chip',
        attrs: { type: 'button', 'data-time': t.id },
        on: { click: () => this.onPick(t.id) },
      }, [
        el('span', { class: 'chip-label', text: t.label }),
        t.res ? el('span', { class: 'chip-res', text: formatRes(t.res) }) : null,
      ]);
      if (t.note) chip.title = t.note;
      this.chipNodes.set(t.id, chip);
      this.chips.append(chip);
    }
    const active = this.activeTime ? this.chipNodes.get(this.activeTime) : undefined;
    active?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }

  private probe(): void {
    const def = this.def;
    if (!def || def.timesMode === 'wayback-local') return;
    const ids = [...this.chipNodes.keys()];
    if (!ids.length) return;
    const center = this.center;
    probeSeries(def, ids, center, (timeId, coverage: Coverage) => {
      if (this.def?.id !== def.id || this.center !== center) return;
      const chip = this.chipNodes.get(timeId);
      if (!chip) return;
      chip.classList.toggle('is-empty', coverage === 'empty');
      chip.classList.toggle('is-covered', coverage === 'covered');
      if (coverage === 'empty') chip.title = 'Zde není pokrytí — jiný rok';
    });
  }
}
